import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  ELITE_WAR_DEFAULT_LAYOUT_ID,
  ELITE_WAR_VIEWBOX,
  ELITE_WAR_LAYOUTS,
  getEliteWarLayoutById,
  isEliteWarLayoutId,
} from "./eliteWarLayouts";

function normalizePlannerState(rawState = {}) {
  const base = rawState && typeof rawState === "object" ? rawState : {};
  const layoutId = isEliteWarLayoutId(base.layoutId)
    ? String(base.layoutId)
    : ELITE_WAR_DEFAULT_LAYOUT_ID;
  return {
    ...base,
    layoutId,
  };
}

export default function EliteWarRealtimePlanner({
  selectedEvent,
  canWriteEvents = false,
  onPlannerStatePatch,
}) {
  const [plannerState, setPlannerState] = useState(() =>
    normalizePlannerState(selectedEvent?.plannerState),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setPlannerState(normalizePlannerState(selectedEvent?.plannerState));
  }, [selectedEvent?._id, selectedEvent?.plannerState]);

  const activeLayout = useMemo(
    () => getEliteWarLayoutById(plannerState?.layoutId),
    [plannerState?.layoutId],
  );
  const nodeById = useMemo(() => {
    const out = {};
    (activeLayout?.nodes || []).forEach((node) => {
      out[String(node.id)] = node;
    });
    return out;
  }, [activeLayout]);
  const leftSpawnX = 3.8;
  const rightSpawnX = ELITE_WAR_VIEWBOX.width - 3.8;
  const leftSpawnY = Number(activeLayout?.leftSpawnY || 25);
  const rightSpawnY = Number(activeLayout?.rightSpawnY || 25);

  const saveLayout = useCallback(
    async (layoutId) => {
      if (!selectedEvent?._id || !canWriteEvents) return;
      const command = {
        commandType: "planner.set_state",
        payload: { layoutId },
        clientTimestamp: new Date().toISOString(),
        idempotencyKey: `elite-layout-${selectedEvent._id}-${layoutId}-${Date.now()}`,
      };

      setIsSaving(true);
      try {
        const response = await axiosInstance.post(
          `/events/${selectedEvent._id}/planner/commands`,
          { commands: [command] },
        );
        const returnedState = response?.data?.state;
        const nextState = normalizePlannerState({
          ...plannerState,
          ...(returnedState && typeof returnedState === "object" ? returnedState : {}),
          layoutId,
        });
        setPlannerState(nextState);
        if (typeof onPlannerStatePatch === "function") {
          onPlannerStatePatch(nextState);
        }
      } catch (error) {
        window.alert(
          typeof error === "string" ? error : "Failed to save Elite War map layout.",
        );
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [canWriteEvents, onPlannerStatePatch, plannerState, selectedEvent?._id],
  );

  const handleSelectLayout = useCallback(
    async (layoutId) => {
      if (!isEliteWarLayoutId(layoutId)) return;
      if (String(plannerState?.layoutId || "") === String(layoutId)) return;
      const previousLayoutId = String(plannerState?.layoutId || ELITE_WAR_DEFAULT_LAYOUT_ID);
      const optimisticState = normalizePlannerState({
        ...plannerState,
        layoutId,
      });
      setPlannerState(optimisticState);
      try {
        await saveLayout(layoutId);
      } catch (_) {
        setPlannerState((prev) => normalizePlannerState({ ...prev, layoutId: previousLayoutId }));
      }
    },
    [plannerState, saveLayout],
  );

  if (!activeLayout) {
    return (
      <div className="events-card events-panel-shell">
        <div className="events-panel-header">
          <h4>Elite War Realtime Planner</h4>
          <span>45-minute battlefield coordination workspace.</span>
        </div>
        <p className="events-muted-text">No layout configuration available.</p>
      </div>
    );
  }

  return (
    <div className="events-card events-panel-shell">
      <div className="events-panel-header">
        <h4>Elite War Realtime Planner</h4>
        <span>{activeLayout.nodes.length} strongholds</span>
      </div>

      <p className="events-muted-text">
        {selectedEvent?.displayLabel || selectedEvent?.name || "Selected event"} - 45-minute realtime battle map.
      </p>

      <div className="events-elite-layout-grid">
        {ELITE_WAR_LAYOUTS.map((layout) => {
          const isActive = String(layout.id) === String(activeLayout.id);
          return (
            <button
              key={layout.id}
              type="button"
              className={`events-elite-layout-option ${isActive ? "active" : ""}`}
              onClick={() => handleSelectLayout(layout.id)}
              disabled={isSaving || !canWriteEvents}
            >
              <strong>{layout.label}</strong>
              <span>{layout.description}</span>
            </button>
          );
        })}
      </div>

      <div className="events-elite-map-wrap">
        <svg
          className="events-elite-map-svg"
          viewBox={`0 0 ${ELITE_WAR_VIEWBOX.width} ${ELITE_WAR_VIEWBOX.height}`}
          role="img"
          aria-label={`${activeLayout.label} battlefield layout`}
        >
          <rect
            x="0"
            y="0"
            width={ELITE_WAR_VIEWBOX.width}
            height={ELITE_WAR_VIEWBOX.height}
            rx="4"
            className="events-elite-map-bg"
          />

          <g className="events-elite-map-links">
            {activeLayout.edges.map(([left, right]) => {
              const leftNode = nodeById[left];
              const rightNode = nodeById[right];
              if (!leftNode || !rightNode) return null;
              return (
                <line
                  key={`${left}-${right}`}
                  x1={leftNode.x}
                  y1={leftNode.y}
                  x2={rightNode.x}
                  y2={rightNode.y}
                />
              );
            })}
            {(activeLayout?.leftBaseLinks || []).map((nodeId) => {
              const node = nodeById[nodeId];
              if (!node) return null;
              return (
                <line
                  key={`left-base-${nodeId}`}
                  className="events-elite-map-base-link"
                  x1={leftSpawnX}
                  y1={leftSpawnY}
                  x2={node.x}
                  y2={node.y}
                />
              );
            })}
            {(activeLayout?.rightBaseLinks || []).map((nodeId) => {
              const node = nodeById[nodeId];
              if (!node) return null;
              return (
                <line
                  key={`right-base-${nodeId}`}
                  className="events-elite-map-base-link"
                  x1={rightSpawnX}
                  y1={rightSpawnY}
                  x2={node.x}
                  y2={node.y}
                />
              );
            })}
          </g>

          <g className="events-elite-map-base-left">
            <circle cx={leftSpawnX} cy={leftSpawnY} r="1.7" />
            <circle cx={leftSpawnX} cy={leftSpawnY} r="0.8" />
            <text x={leftSpawnX - 2.1} y={leftSpawnY - 6.1}>{activeLayout.leftSpawnLabel || ""}</text>
          </g>
          <g className="events-elite-map-base-right">
            <circle cx={rightSpawnX} cy={rightSpawnY} r="1.7" />
            <circle cx={rightSpawnX} cy={rightSpawnY} r="0.8" />
            <text x={rightSpawnX + 2.1} y={rightSpawnY - 6.1}>{activeLayout.rightSpawnLabel || ""}</text>
          </g>

          <g className="events-elite-map-nodes">
            {activeLayout.nodes.map((node) => (
              <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
                <rect className="events-elite-map-fort-body" x="-1.85" y="-1.5" width="3.7" height="2.8" rx="0.32" />
                <rect className="events-elite-map-fort-top" x="-1.62" y="-2.1" width="0.84" height="0.65" />
                <rect className="events-elite-map-fort-top" x="0.78" y="-2.1" width="0.84" height="0.65" />
                <text x="0" y="0.05">
                  {node.label}
                </text>
              </g>
            ))}
          </g>
        </svg>
      </div>

      <p className="events-muted-text">
        {canWriteEvents
          ? (isSaving ? "Saving layout..." : "Layout selection is saved for this event.")
          : "Read-only view. You need write permission to update the saved layout."}
      </p>
    </div>
  );
}
